/* Borsa pozisyonu aksiyon panelleri — TP/SL, Hedge TP/SL, Kaldıraç, Onaylı buton */
import { useEffect, useRef, useState } from "react";

export const opBtn = (variant = "ghost") => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  height: 28,
  padding: "0 10px",
  borderRadius: 7,
  fontFamily: "var(--fm)",
  fontSize: 11,
  fontWeight: 600,
  cursor: "pointer",
  whiteSpace: "nowrap",
  transition: "background-color .15s, color .15s, border-color .15s",
  ...(variant === "danger"
    ? { border: "1px solid rgba(255,77,77,0.35)", background: "rgba(255,77,77,0.08)", color: "var(--down)" }
    : variant === "accent"
    ? { border: "1px solid var(--acc-line)", background: "var(--acc-dim)", color: "var(--acc)" }
    : { border: "1px solid var(--line)", background: "rgba(255,255,255,0.03)", color: "var(--muted)" }),
});

const inputStyle = {
  width: 110,
  height: 30,
  padding: "0 10px",
  borderRadius: 7,
  border: "1px solid var(--line)",
  background: "rgba(0,0,0,0.3)",
  color: "var(--txt)",
  fontFamily: "var(--fm)",
  fontSize: 12,
  outline: "none",
};

const panelStyle = {
  padding: "12px 16px",
  borderTop: "1px dashed var(--line)",
  background: "rgba(255,255,255,0.015)",
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 10,
  fontFamily: "var(--fm)",
  fontSize: 11,
};

const labelStyle = {
  fontSize: 9,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--faint)",
  display: "block",
  marginBottom: 4,
};

/** İki aşamalı onay butonu: 1. tık → "Emin misin?", 2. tık → onConfirm. */
export function ConfirmButton({ label, onConfirm, busy, testId, variant = "danger" }) {
  const [armed, setArmed] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const click = () => {
    if (busy) return;
    if (!armed) {
      setArmed(true);
      timer.current = setTimeout(() => setArmed(false), 4000);
      return;
    }
    clearTimeout(timer.current);
    setArmed(false);
    onConfirm();
  };
  return (
    <button
      type="button"
      onClick={click}
      disabled={busy}
      data-testid={testId}
      style={{
        ...opBtn(variant),
        ...(armed ? { background: "rgba(255,77,77,0.2)", fontWeight: 700 } : {}),
        opacity: busy ? 0.6 : 1,
      }}
    >
      {busy ? "Gönderiliyor…" : armed ? "Emin misin? Tekrar tıkla" : label}
    </button>
  );
}

/** Tek bacak için TP + SL fiyat girişi. */
export function TpslPanel({ leg, busy, onSubmit, onClose }) {
  const [tp, setTp] = useState("");
  const [sl, setSl] = useState("");
  const valid = (tp && Number(tp) > 0) || (sl && Number(sl) > 0);
  return (
    <div style={panelStyle} data-testid={`tpsl-panel-${leg.exchange}`}>
      <div>
        <span style={labelStyle}>TP fiyatı (kâr al)</span>
        <input
          style={inputStyle}
          inputMode="decimal"
          placeholder={`örn. ${leg.mark_price ? Number(leg.mark_price).toFixed(2) : "—"}`}
          value={tp}
          onChange={(e) => setTp(e.target.value)}
          data-testid="tpsl-tp-input"
        />
      </div>
      <div>
        <span style={labelStyle}>SL fiyatı (zarar kes)</span>
        <input
          style={inputStyle}
          inputMode="decimal"
          placeholder="boş bırakılabilir"
          value={sl}
          onChange={(e) => setSl(e.target.value)}
          data-testid="tpsl-sl-input"
        />
      </div>
      <button
        type="button"
        style={{ ...opBtn("accent"), opacity: valid && !busy ? 1 : 0.5, marginTop: 14 }}
        disabled={!valid || busy}
        onClick={() =>
          onSubmit({
            take_profit: tp ? Number(tp) : null,
            stop_loss: sl ? Number(sl) : null,
          })
        }
        data-testid="tpsl-submit"
      >
        {busy ? "Gönderiliyor…" : "Emirleri Yerleştir"}
      </button>
      <button type="button" style={{ ...opBtn(), marginTop: 14 }} onClick={onClose}>
        Vazgeç
      </button>
      <div style={{ flexBasis: "100%", color: "var(--faint)", lineHeight: 1.5 }}>
        Pozisyonun tamamı tetik fiyatında market emriyle kapanır (mark fiyat baz).
        Yeni emir, semboldeki eski TP/SL emirlerini iptal eder.
      </div>
    </div>
  );
}

/** Hedge çifti için tek tetikli strateji TP/SL'i. */
export function HedgeTpslPanel({ pair, busy, onSubmit, onClose }) {
  const [price, setPrice] = useState("");
  const valid = price && Number(price) > 0;
  const dkShort = pair.dk.side === "SHORT";
  const dirText = dkShort ? "ÜSTÜNDE" : "ALTINDA";
  return (
    <div style={panelStyle} data-testid="hedge-tpsl-panel">
      <div>
        <span style={labelStyle}>Tetik fiyatı (iki borsada eşit)</span>
        <input
          style={{ ...inputStyle, width: 140 }}
          inputMode="decimal"
          placeholder={`güncel: $${Number(pair.dk.mark_price || 0).toFixed(2)}`}
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          data-testid="hedge-tpsl-price-input"
        />
      </div>
      <button
        type="button"
        style={{ ...opBtn("accent"), opacity: valid && !busy ? 1 : 0.5, marginTop: 14 }}
        disabled={!valid || busy}
        onClick={() => onSubmit(Number(price))}
        data-testid="hedge-tpsl-submit"
      >
        {busy ? "Gönderiliyor…" : "Hedge TP/SL Kur"}
      </button>
      <button type="button" style={{ ...opBtn(), marginTop: 14 }} onClick={onClose}>
        Vazgeç
      </button>
      <div style={{ flexBasis: "100%", color: "var(--faint)", lineHeight: 1.6 }}>
        <b style={{ color: "var(--muted)" }}>Strateji:</b> Aynı fiyata Darkex'e{" "}
        <b style={{ color: "var(--down)" }}>STOP</b> (zarar realize → bonus
        kazanılır), {pair.counter.exchange === "BINANCE" ? "Binance" : pair.counter.exchange}'e{" "}
        <b style={{ color: "var(--up)" }}>TP</b> (kâr realize → ana para garanti).
        Tetik fiyatı güncel fiyatın <b>{dirText}</b> olmalı (Darkex zarar yönü).
      </div>
    </div>
  );
}

/** Kaldıraç değiştirme. */
export function LeveragePanel({ leg, busy, onSubmit, onClose }) {
  const [lev, setLev] = useState(leg.leverage || 10);
  const valid = Number(lev) >= 1 && Number(lev) <= 125;
  return (
    <div style={panelStyle} data-testid={`leverage-panel-${leg.exchange}`}>
      <div>
        <span style={labelStyle}>Kaldıraç (1-125×)</span>
        <input
          style={{ ...inputStyle, width: 80 }}
          type="number"
          min={1}
          max={125}
          value={lev}
          onChange={(e) => setLev(e.target.value)}
          data-testid="leverage-input"
        />
      </div>
      {[5, 10, 20, 50].map((v) => (
        <button
          key={v}
          type="button"
          style={{ ...opBtn(Number(lev) === v ? "accent" : "ghost"), marginTop: 14 }}
          onClick={() => setLev(v)}
        >
          {v}×
        </button>
      ))}
      <button
        type="button"
        style={{ ...opBtn("accent"), opacity: valid && !busy ? 1 : 0.5, marginTop: 14 }}
        disabled={!valid || busy}
        onClick={() => onSubmit(Number(lev))}
        data-testid="leverage-submit"
      >
        {busy ? "Gönderiliyor…" : "Uygula"}
      </button>
      <button type="button" style={{ ...opBtn(), marginTop: 14 }} onClick={onClose}>
        Vazgeç
      </button>
    </div>
  );
}
